import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { getPasswordStrength, PasswordRuleKey } from '../../utils/passwordStrength';
import {
  SuccessIcon,
  CrossCloseIcon,
  HideInputIcon,
  ShowInputIcon,
  InfoIcon,
} from '@common/utils/icons/icons';
import TextInput from '@common/components/Inputs/TextInput';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { LightTooltip } from '@common/components/General/Tooltips';
import ValidationResult from '@common/interfaces/api/validation-result';
import { useTheme } from '@mui/material';

interface PasswordInputProps {
  password: string;
  isSetField?: boolean;
  label: string;
  name: string;
  validationResult?: ValidationResult;
  onChange: (newValue: string) => void;
}

const STRENGTH_LABEL_KEYS = [
  'password-strength-weak',
  'password-strength-weak',
  'password-strength-fair',
  'password-strength-good',
  'password-strength-strong',
];

const RULES: Array<{ key: PasswordRuleKey; labelKey: string }> = [
  { key: 'minLength', labelKey: 'password-rule-min-length' },
  { key: 'uppercase', labelKey: 'password-rule-uppercase' },
  { key: 'lowercase', labelKey: 'password-rule-lowercase' },
  { key: 'digit', labelKey: 'password-rule-digit' },
  { key: 'special', labelKey: 'password-rule-special' },
];

const PasswordInput: React.FC<PasswordInputProps> = ({
  password,
  label,
  name,
  isSetField,
  validationResult,
  onChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { score, missingRules } = getPasswordStrength(password);

  const [isVisible, setIsVisible] = useState(false);

  const togglePasswordVisibility = () => setIsVisible((prevState) => !prevState);

  const passwordChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  return (
    <Stack spacing={0.5} width="100%">
      <TextInput
        type={isVisible ? 'string' : 'password'}
        label={label}
        value={password}
        onChange={passwordChangeHandler}
        validationResult={validationResult}
        endAdornment={
          <InputAdornment position="end">
            {isSetField && (
              <LightTooltip
                title={
                  <Stack spacing={0.25}>
                    {RULES.map(({ key, labelKey }) => {
                      const isMet = !missingRules.includes(key);
                      return (
                        <Stack
                          key={key}
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          data-testid={`password-rule-${labelKey.replace('password-rule-', '')}-${name}`}
                          data-met={isMet}
                        >
                          {isMet ? (
                            <SuccessIcon fontSize="small" color="success" />
                          ) : (
                            <CrossCloseIcon fontSize="small" color="disabled" />
                          )}
                          <Typography variant="caption">{t(labelKey)}</Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                }
              >
                <InfoIcon data-testid={`password-info-${name}`} />
              </LightTooltip>
            )}
            <IconButton onClick={togglePasswordVisibility} edge="end">
              {isVisible ? (
                <HideInputIcon data-testid={`toggle-password-visibility-${name}`} />
              ) : (
                <ShowInputIcon data-testid={`toggle-password-visibility-${name}`} />
              )}
            </IconButton>
          </InputAdornment>
        }
      />
      {isSetField && (
        <React.Fragment>
          <Stack direction="row" spacing={0.5}>
            {[
              theme.palette.error.main,
              theme.palette.warning.main,
              '#fbc02d',
              theme.palette.success.main,
            ].map((color, index) => (
              <Box
                key={color}
                data-testid={`password-strength-segment-${index}-${name}`}
                sx={{
                  height: 4,
                  flex: 1,
                  borderRadius: 1,
                  backgroundColor: index < score ? color : 'action.disabledBackground',
                }}
              />
            ))}
          </Stack>
          <Typography variant="caption">
            {t(STRENGTH_LABEL_KEYS[score])}
            {score < 3 && ` - ${t('password-must-meet-requirements')}`}
          </Typography>
        </React.Fragment>
      )}
    </Stack>
  );
};

export default PasswordInput;
